// type
import {
  Brand,
  OverviewCharts,
  FbAdsCount,
  PaidAds,
  Product,
  ProductsSummary,
  ReviewTrackerDetail,
  SerpstatAds,
  SerpstatKeywords,
  TrafficData,
  ViabilityDetail,
  CountryTrafficGrowth,
  BrandViewStatus,
  NotificationRequest,
  FullBrand,
} from "~src/types/brand";
import { CollectionResult } from "~src/types/common";
import { FbAd } from "~src/types/ad";
import { FeaturedProduct as BrandProduct } from "~src/types/product";
// api
import { ApiTypes, api } from "../common";
import { AnalyticsData } from "~src/pages/brands/Detail/TrafficAndRevenue";
import { getLatestData } from "~src/sections/brands/trafficAndRevenue/Graphs/utils";
import { CompetitorAds } from "~src/sections/competitorAnalytics/CompetitorAnalytics";
import { BrandFormData } from "~src/pages/brands/AddBrand";

const BRAND_PATHS: ApiTypes.API = {
  brand: {
    path: `api/brands/`,
    method: ApiTypes.HTTPMethod.GET,
  },
  unlockViability: {
    path: `api/subscription/unlock-viability/`,
    method: ApiTypes.HTTPMethod.POST,
  },
  notificationRequest: {
    path: `api/notifications/requests/`,
    method: ApiTypes.HTTPMethod.GET,
  },
  notifyMe: {
    path: `api/notifications/notify-me/`,
    method: ApiTypes.HTTPMethod.POST,
  },
  brandViewStatus: {
    path: `api/subscription/brand-view-status/`,
    method: ApiTypes.HTTPMethod.GET,
  },
  trackBrand: {
    path: `fc/track/`,
    method: ApiTypes.HTTPMethod.POST,
  },
  fullBrand: {
    path: "api/brands/",
    method: ApiTypes.HTTPMethod.GET,
  },
  track: {
    path: "api/user/track-store/",
    method: ApiTypes.HTTPMethod.POST,
  },
  overviewCharts: {
    path: "api/analytics/brand-metrics/",
    method: ApiTypes.HTTPMethod.GET,
  },
  reviewTracker: {
    path: `/api/brand-reviews`,
    method: ApiTypes.HTTPMethod.GET,
  },
  serpstatKeywords: {
    path: "api/keyword-data/",
    method: ApiTypes.HTTPMethod.GET,
  },
  serpstatAds: {
    path: "api/serpstat/adkeywords/",
    method: ApiTypes.HTTPMethod.POST,
  },
  trafficChart: {
    path: "api/analytics/brand-traffic-revenue/",
    method: ApiTypes.HTTPMethod.GET,
  },
  trafficSourceChart: {
    path: "api/traffic-source/",
    method: ApiTypes.HTTPMethod.GET,
  },
  fbAds: {
    path: "api/ads/facebook-ads",
    method: ApiTypes.HTTPMethod.GET,
  },
  fbAdsReview: {
    path: "api/analytics/facebook-ads/",
    method: ApiTypes.HTTPMethod.GET,
  },
  fbAdsCount: {
    path: "api/ads/facebook-ads-count/",
    method: ApiTypes.HTTPMethod.GET,
  },
  countryTrafficGrowth: {
    path: "api/report/traffic-growth/",
    method: ApiTypes.HTTPMethod.GET,
  },
  products: {
    path: "/api/products/shopify-products-variants/",
    method: ApiTypes.HTTPMethod.GET,
  },
  products_old: {
    path: "search/shopify-products/",
    method: ApiTypes.HTTPMethod.GET,
  },
  productsSummary: {
    path: "api/products/summary",
    method: ApiTypes.HTTPMethod.GET,
  },
  productsSummary_old: {
    path: "search/shopify-products/product_summary",
    method: ApiTypes.HTTPMethod.GET,
  },
  bestSellingProducts: {
    path: "api/report/brand-products/?best_seller=true",
    method: ApiTypes.HTTPMethod.GET,
  },
  paidAds: {
    path: "api/report/paid-ads-platforms/",
    method: ApiTypes.HTTPMethod.GET,
  },
  viabilityDetail: {
    path: "api/report/viability-report/",
    method: ApiTypes.HTTPMethod.GET,
  },
  toggleReportPublished: {
    path: "api/report/toggle-report-published/",
    method: ApiTypes.HTTPMethod.PATCH,
  },
  wappalyzer: {
    path: "api/wappalyzer-upload/",
    method: ApiTypes.HTTPMethod.POST,
  },
  brandUpdate: {
    path: "api/update-brand/",
    method: ApiTypes.HTTPMethod.PATCH,
  },
  featureBrand: {
    path: "api/user/feature-brand/",
    method: ApiTypes.HTTPMethod.POST,
  },
  trafficSourceMetrics: {
    path: "api/analytics/traffic-source/",
    method: ApiTypes.HTTPMethod.GET,
  },
  audienceDemographics: {
    path: "api/analytics/audience-demographic/",
    method: ApiTypes.HTTPMethod.GET,
  },
  socialMedia: {
    path: "api/analytics/social-media/",
    method: ApiTypes.HTTPMethod.GET,
  },
  createBrand: {
    path: "data-endpoints/create-brand/",
    method: ApiTypes.HTTPMethod.POST,
  },
  brandScrapper: {
    path: "api/brand/run-brand-scrapper/",
    method: ApiTypes.HTTPMethod.POST,
  },
};

type FeaturedBrandsResponse = {
  count: number;
  next: string;
  previous: string | null;
  results: Brand[];
};

export const createBrand = async (formData: BrandFormData) => {
  const endpoint = BRAND_PATHS.createBrand;
  const response = await api({
    method: endpoint.method,
    url: endpoint.path,
    data: formData,
  });

  return response.data;
};

export const runBrandScrapper = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.brandScrapper;
  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}/${brand_id}`,
  });

  return response.data;
};

export const getBrandSocialMedia = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.socialMedia;
  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}${brand_id}`,
  });

  return getLatestData(response.data);
};

export const getAudienceDemographics = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.audienceDemographics;
  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}${brand_id}`,
  });
  return response.data;
};

export const getTrafficSourceMetricsChart = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.trafficSourceMetrics;
  const response = await api<AnalyticsData>({
    method: endpoint.method,
    url: `${endpoint.path}${brand_id}`,
  });
  return response.data;
};

export const getTrafficSourceChart = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.trafficSourceChart;
  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}${brand_id}`,
  });
  return response.data;
};

export const featureBrand = async (brand_id: number, is_featured: number) => {
  const endpoint = BRAND_PATHS.featureBrand;

  const response = await api({
    method: endpoint.method,
    url: endpoint.path,
    data: { brand_id, is_featured },
  });
  return response.data;
};

export const updateBrand = async (
  payload: ViabilityDetail | undefined,
) => {
  const endpoint = BRAND_PATHS.brandUpdate;

  const response = await api<ViabilityDetail>({
    method: endpoint.method,
    url: endpoint.path,
    data: payload,
  });
  return response.data;
};

export const triggerWappalyzer = async (payload: FormData) => {
  const endpoint = BRAND_PATHS.wappalyzer;
  const response = await api({
    method: endpoint.method,
    headers: { "Content-Type": "multipart/form-data" },
    url: endpoint.path,
    data: payload,
  });
  return response.data;
};

export const toggleReportPublished = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.toggleReportPublished;

  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}${brand_id}/`,
  });
  return response.data;
};

export const getFeaturedBrands = async (
  page: number,
  page_size: number,
  is_featured: number,
  is_published: boolean,
  options?: { signal?: AbortSignal }
) => {
  const endpoint = BRAND_PATHS.brand;
  const response = await api<FeaturedBrandsResponse>({
    method: endpoint.method,
    url: `${endpoint.path}?page=${page}&page_size=${page_size}&is_featured=${is_featured}&is_published=${is_published}`,
    signal: options?.signal
  });
  return response.data;
};

export const getBrand = async (brandId: number) => {
  const endpoint = BRAND_PATHS.brand;
  const response = await api({
    method: endpoint.method,
    url: endpoint.path,
    params: { brand_id: brandId },
  });
  return response.data.results[0];
};

export const viabilityUnlock = async (brandId: number) => {
  const endpoint = BRAND_PATHS.unlockViability;
  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}${brandId}/`,
  });
  return response.data;
};

export const getNotificationRequest = async (brandId: number) => {
  const endpoint = BRAND_PATHS.notificationRequest;
  const response = await api<NotificationRequest[]>({
    method: endpoint.method,
    url: `${endpoint.path}?brand_id=${brandId}`,
  });
  return response.data;
};

export const notifyMe = async (
  brandId: number,
  brandName: string,
  eventType: string,
) => {
  const endpoint = BRAND_PATHS.notifyMe;
  const response = await api({
    method: endpoint.method,
    url: `${endpoint.path}?brand_id=${brandId}`,
    data: { brand_id: brandId, brand_name: brandName, event_type: eventType },
  });
  return response.data;
};

export const trackBandView = async (brandId: number, viewType: number) => {
  const endpoint = BRAND_PATHS.trackBrand;
  const response = await api({
    method: endpoint.method,
    url: endpoint.path,
    data: { brand_id: brandId, view_type: viewType },
  });
  return response.data;
};

export const getBrandViewStatus = async (brandId: number) => {
  const endpoint = BRAND_PATHS.brandViewStatus;
  const response = await api<BrandViewStatus>({
    method: endpoint.method,
    url: `${endpoint.path}?brand_id=${brandId}`,
  });

  return response.data;
};

type FullBrandResponse = {
  count: number;
  next: string;
  previous: string;
  results: FullBrand[];
};

export const getFullBrand = async (brandId: number) => {
  const endpoint = BRAND_PATHS.fullBrand;
  const response = await api<FullBrandResponse>({
    method: endpoint.method,
    url: endpoint.path,
    params: {
      brand_id: brandId,
    },
  });

  return response.data.results[0];
};

export const trackBrand = async (brandId: number, shouldTrack: boolean) => {
  const endpoint = BRAND_PATHS.track;

  const response = await api({
    method: endpoint.method,
    url: endpoint.path,
    data: {
      brand_id: brandId,
      should_track: shouldTrack,
    },
  });
  return response.data;
};

export const getOverviewCharts = async (brandId: number, duration: string) => {
  const endpoint = BRAND_PATHS.overviewCharts;
  const response = await api<OverviewCharts>({
    method: endpoint.method,
    url: `${endpoint.path}${brandId}/`,
    params: { duration },
  });
  return response.data;
};

export const getReviewTrackerData = async (
  brandId: number,
  duration: number,
) => {
  const endpoint = BRAND_PATHS.reviewTracker;
  const response = await api<ReviewTrackerDetail>({
    method: endpoint.method,
    url: `${endpoint.path}/${brandId}`,
    params: { duration },
  });
  return response.data;
};

export const getSerpstatKeywords = async (brandId: number) => {
  const endpoint = BRAND_PATHS.serpstatKeywords;
  const response = await api<SerpstatKeywords[]>({
    method: endpoint.method,
    url: `${endpoint.path}${brandId}`,
  });
  return response.data;
};

export const getTrafficChart = async (brandId: number) => {
  const endpoint = BRAND_PATHS.trafficChart;
  const response = await api<TrafficData>({
    method: endpoint.method,
    url: `${endpoint.path}${brandId}`,
  });
  return response.data;
};

export const getSerpstatAds = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.serpstatAds;
  const response = await api<SerpstatAds[]>({
    method: endpoint.method,
    url: endpoint.path,
    data: { brand_id },
  });
  return response.data;
};

export const getCountryTrafficGrowth = async (brandId: number) => {
  const endpoint = BRAND_PATHS.countryTrafficGrowth;
  const response = await api<CountryTrafficGrowth[]>({
    method: endpoint.method,
    url: `${endpoint.path}${brandId}`,
  });
  return response.data;
};

export const getFbAds = async (
  page = 1,
  page_size = 20,
  brand_id: string,
  ordering: string,
) => {
  const endpoint = BRAND_PATHS.fbAds;
  const response = await api<CollectionResult<FbAd>>({
    method: endpoint.method,
    url: endpoint.path,
    params: { page, page_size, brand_id, ordering },
  });

  return response.data;
};

export const getFbAdsReview = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.fbAdsReview;
  const response = await api<CompetitorAds>({
    method: endpoint.method,
    url: endpoint.path,
    params: { brand_ids: brand_id },
  });
  return response.data;
};

export const getFbAdsCount = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.fbAdsCount;
  const response = await api<{ status: number; data: FbAdsCount[] }>({
    method: endpoint.method,
    url: `${endpoint.path}${brand_id}`,
  });

  return response.data;
};

export const getProducts = async (
  brand_ids: number,
  page: number,
  page_size: number,
  order_by: string,
) => {
  const endpoint = BRAND_PATHS.products;
  const response = await api<CollectionResult<Product>>({
    method: endpoint.method,
    url: endpoint.path,
    params: { page, page_size, order_by, brand_ids },
  });

  return response.data;
};

export const getProductsSummary = async (brandId: number | string) => {
  const endpoint = BRAND_PATHS.productsSummary;
  const response = await api<ProductsSummary>({
    method: endpoint.method,
    url: `${endpoint.path}/${brandId}/`,
  });

  return response.data;
};

export const getBestSellingProducts = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.bestSellingProducts;
  const response = await api<{ products: BrandProduct[] }>({
    method: endpoint.method,
    url: endpoint.path,
    params: { brand_id },
  });

  return response.data;
};

export const getPaidAds = async (brand_id: number) => {
  const endpoint = BRAND_PATHS.paidAds;
  const response = await api<PaidAds>({
    method: endpoint.method,
    url: endpoint.path,
    params: { brand_id },
  });

  return response.data;
};

export const getViabilityReportDetail = async (brandId: number) => {
  const endpoint = BRAND_PATHS.viabilityDetail;
  const response = await api<{ data: ViabilityDetail }>({
    method: endpoint.method,
    url: `${endpoint.path}${brandId}/`,
  });

  return response.data.data;
};
